<template>
  <div class="console-side-menu" :class="{ collapsed }" role="navigation" aria-label="主导航">
    <!-- 顶部 logo（替代原本顶栏位置） -->
    <div class="side-logo" v-show="!collapsed">
      <img
        class="side-logo-img"
        src="@/assets/home/pageLogo2.webp"
        alt="电气火灾超前预警平台"
      />
    </div>

    <!-- 看板入口（一级菜单，不可展开） -->
    <div
      class="dashboard-entry"
      :class="{ active: isDashboard }"
      @click="goDashboard"
      @keydown.enter="goDashboard"
      v-show="!collapsed"
      role="menuitem"
      tabindex="0"
    >
      <span class="dashboard-entry-icon" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.6"/>
          <rect x="13" y="3" width="8" height="5" rx="1.5" stroke="currentColor" stroke-width="1.6"/>
          <rect x="3" y="13" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.6"/>
          <rect x="13" y="13" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.6"/>
        </svg>
      </span>
      <span class="dashboard-entry-text">看板</span>
    </div>

    <div class="menu-scroll" v-show="!collapsed">
      <!-- 菜单分组 -->
      <div v-for="group in filteredMenuTree" :key="group.groupKey" class="menu-group">
        <div class="group-header" @click="handleGroupClick(group)">
          <span class="arrow" :class="{ expanded: expandedGroups[group.groupKey] }">▼</span>
          {{ group.group }}
        </div>
        <div v-show="expandedGroups[group.groupKey]" class="group-children">
          <!-- 普通子项 -->
          <template v-if="group.children">
            <div
              v-for="item in group.children"
              :key="item.path"
              class="menu-item"
              :class="{ active: isActive(item.path) }"
              @click="navigate(item.path)"
            >
              <span class="menu-item-icon" v-html="item.icon"></span>
              <span class="menu-item-text">{{ item.name }}</span>
            </div>
          </template>
          <!-- 带分割线的子项（系统设置专用） -->
          <template v-if="group.sections">
            <template v-for="section in group.sections">
              <div :key="section.title" class="section-divider">{{ section.title }}</div>
              <div
                v-for="item in section.children"
                :key="item.path"
                class="menu-item section-item"
                :class="{ active: isActive(item.path) }"
                @click="navigate(item.path)"
              >
                <span class="menu-item-icon" v-html="item.icon"></span>
                <span class="menu-item-text">{{ item.name }}</span>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>

    <!-- 底部用户区域 -->
    <div
      class="side-user"
      v-show="!collapsed"
      @mouseenter="showUserMenu = true"
      @mouseleave="showUserMenu = false"
    >
      <div class="side-user-info">
        <div class="side-user-avatar">{{ (userName || 'U')[0] }}</div>
        <div class="side-user-meta">
          <div class="side-user-name">{{ userName || '未登录' }}</div>
          <div class="side-user-role">{{ loginName || '' }}</div>
        </div>
      </div>
      <transition name="side-user-popup">
        <div class="side-user-popup" v-if="showUserMenu">
          <div class="side-user-popup-body" @click="goProfile">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.6"/>
              <path d="M4 20c0-3.314 3.582-6 8-6s8 2.686 8 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
            <span>基本信息</span>
            <svg class="side-popup-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="side-user-popup-divider"></div>
          <div class="side-user-popup-footer" @click="logout">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M21 12H9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>退出登录</span>
          </div>
        </div>
      </transition>
    </div>

    <div class="collapse-btn" @click="$emit('toggle-collapse')">
      <i :class="collapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-left'"></i>
    </div>
  </div>
</template>

<script>
import Bus from '@/utils/bus.js'
import auth from '@/scripts/auth.js'

// ponytail: 每个子项配一个 18×18 inline SVG 图标，stroke 继承 currentColor
const S = (d) => `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="${d}" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`

const MENU_TREE = [
  {
    group: '监测管理',
    groupKey: 'SITES',
    enableTabs: true,
    children: [
      { name: '诊断详情', path: '/console/sites/list',       perm: 'MONITOR_MANAGE',       icon: S('M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z') },
      { name: '数据详情',   path: '/console/sites/data',       perm: 'MONITOR_DATA_DETAIL', icon: S('M4 7V4h16v3M4 7v9a2 2 0 002 2h12a2 2 0 002-2V7M4 7h16M9 12l2 2 4-4') },
      { name: '评估报告',   path: '/console/sites/reports',    perm: 'SAFE_REPORT',         icon: S('M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8') },
    ],
  },
  {
    group: '告警记录',
    groupKey: 'ALERTS',
    enableTabs: false,
    children: [
      { name: '用电告警',   path: '/console/alerts/electrical',   perm: 'ALARM_EVENT',      icon: S('M13 2L3 14h9l-1 8 10-12h-9l1-8z') },
      { name: '环境告警',   path: '/console/alerts/environment',  perm: 'ALARM_PERIPHERAL', icon: S('M3 15a4 4 0 004 4h9a5 5 0 10-4.5-7.2A3 3 0 003 15zM12 7v4M12 15h.01') },
      { name: '隐患预警',   path: '/console/alerts/hazard',       perm: 'ALARM_RISK',       icon: S('M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5') },
      { name: '隐患设备',   path: '/console/alerts/equipment',    perm: 'HIDDEN_EQUIPMENT', icon: S('M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18') },
    ],
  },
  {
    group: '终端设备',
    groupKey: 'DEVICES',
    enableTabs: false,
    children: [
      { name: '设备管理',   path: '/console/devices/manage',  perm: 'DEVICE_MANAGE',    icon: S('M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8') },
      { name: '设备运维',   path: '/console/devices/ops',     perm: 'DEVICE_OPERATION', icon: S('M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z') },
    ],
  },
  {
    group: '能耗分析',
    groupKey: 'ENERGY',
    enableTabs: false,
    children: [
      { name: '能耗总览',   path: '/console/energy/overview',   perm: 'ENERGY_MONITOR', icon: S('M12 20V10M18 20V4M6 20v-4') },
      { name: '用能统计',   path: '/console/energy/statistics', perm: 'ENERGY_MONITOR', icon: S('M18 20V10M12 20V4M6 20v-4M3 12h18') },
      { name: '碳管理',     path: '/console/energy/carbon',     perm: 'ENERGY_MONITOR', icon: S('M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.66 0 3-4.03 3-9s-1.34-9-3-9m0 18c-1.66 0-3-4.03-3-9s1.34-9 3-9m-9 9a9 9 0 019-9') },
      { name: '电能质量',   path: '/console/energy/quality',    perm: 'ENERGY_MONITOR', icon: S('M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5') },
      { name: '用电集抄',   path: '/console/energy/meter',      perm: 'ENERGY_MONITOR', icon: S('M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2') },
    ],
  },
  {
    group: '系统设置',
    groupKey: 'SETTINGS',
    enableTabs: false,
    sections: [
      {
        title: '账号管理',
        children: [
          { name: '用户管理',   path: '/console/settings/users',         perm: 'USER_MANAGE',    icon: S('M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M8.5 7a4 4 0 100-8 4 4 0 000 8zM20 8v6M23 11h-6') },
          { name: '角色管理',   path: '/console/settings/roles',         perm: 'ROLE_MANAGE',    icon: S('M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.32 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z') },
          { name: '组织管理',   path: '/console/settings/orgs',          perm: 'DEPT_MANAGE',    icon: S('M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2zM9 22V12h6v10') },
          { name: '合作方管理', path: '/console/settings/partners',      perm: 'ACCOUNT_MANAGE', icon: S('M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75') },
          { name: '应用管理',   path: '/console/settings/apps',          perm: 'APP_MANAGE',     icon: S('M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2zM10 19h4') },
          { name: '标签管理',   path: '/console/settings/tags',          perm: 'LABEL_MANAGE',   icon: S('M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82zM7 7h.01') },
        ],
      },
      {
        title: '告警配置',
        children: [
          { name: '隐患类型配置', path: '/console/settings/alert-types',   perm: 'ALERT_MANAGE',     icon: S('M12 9v4M12 17h.01M3.34 19L12 3.65 20.66 19H3.34z') },
          { name: '组织隐患配置', path: '/console/settings/hazard-config', perm: 'RISK_CONFIG',      icon: S('M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18') },
          { name: '推送配置',     path: '/console/settings/push',          perm: 'WARN_PUSH_CONFIG', icon: S('M22 12h-4l-3 9L9 3l-3 9H2') },
          { name: '短信推送',     path: '/console/settings/sms',           perm: 'SMS_PUSH_CONFIG',  icon: S('M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2zM8 10h.01M12 10h.01M16 10h.01') },
          { name: '语音推送',     path: '/console/settings/voice',         perm: 'VMS_PUSH_CONFIG',  icon: S('M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8') },
        ],
      },
      {
        title: '审计日志',
        children: [
          { name: '操作日志', path: '/console/settings/logs', perm: 'LOG_MANAGE', icon: S('M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8') },
        ],
      },
    ],
  },
  {
    group: '账号中心',
    groupKey: 'PROFILE',
    enableTabs: false,
    children: [
      { name: '基本信息', path: '/console/profile', perm: 'USER_MANAGE', icon: S('M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 3a4 4 0 100 8 4 4 0 000-8z') },
      { name: '访问密钥', path: '/console/access-key', perm: 'USER_MANAGE', icon: S('M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z') },
    ],
  },
]

export default {
  name: 'ConsoleSideMenu',
  props: {
    collapsed: { type: Boolean, default: false },
  },
  data() {
    return {
      expandedGroups: {},
      menusList: [],
      showUserMenu: false,
      userName: '',
      loginName: '',
    }
  },
  computed: {
    isDashboard() {
      return this.$route.path === '/dashboard' || this.$route.path.startsWith('/dashboard')
    },
    filteredMenuTree() {
      return MENU_TREE
        .map(group => {
          const clone = { ...group }
          if (clone.children) {
            clone.children = clone.children.filter(item =>
              this.menusList.includes(item.perm)
            )
          }
          if (clone.sections) {
            clone.sections = clone.sections
              .map(section => ({
                ...section,
                children: section.children.filter(item =>
                  this.menusList.includes(item.perm)
                ),
              }))
              .filter(section => section.children.length > 0)
          }
          return clone
        })
        .filter(group =>
          (group.children && group.children.length > 0) ||
          (group.sections && group.sections.length > 0)
        )
    },
  },
  methods: {
    isActive(path) {
      return this.$route.path === path || this.$route.path.startsWith(path + '/')
    },
    navigate(path) {
      if (this.$route.path === path) return

      // ponytail: 所有菜单项统一通过 Bus 发 pushrouter 让 ConsoleTabs 添加/切换到对应标签
      var group = MENU_TREE.find(function (g) { return g.children && g.children.some(function (c) { return c.path === path }) })
      var item = null
      if (group && group.children) {
        item = group.children.find(function (c) { return c.path === path })
      }
      if (!item && group && group.sections) {
        item = group.sections.reduce(function (found, s) {
          return found || s.children.find(function (c) { return c.path === path })
        }, null)
      }
      if (item) {
        Bus.$emit('pushrouter', [item.name, '', item.name, path])
      } else {
        this.$router.push(path)
      }
    },
    handleGroupClick(group) {
      this.toggleGroup(group.groupKey)
      // ponytail: 单子项分组（如账号中心），点击分组名时折叠/展开 + 跳转；首次展开时跳转
      if (group.children && group.children.length === 1 && this.expandedGroups[group.groupKey]) {
        const path = group.children[0].path
        if (this.$route.path !== path) {
          this.$router.push(path)
        }
      }
    },
    toggleGroup(key) {
      this.expandedGroups = { ...this.expandedGroups, [key]: !this.expandedGroups[key] }
    },
    goDashboard() {
      this.$router.push('/dashboard')
    },
    goProfile() {
      this.showUserMenu = false
      this.$router.push('/console/profile')
    },
    logout() {
      this.showUserMenu = false
      auth.logout(this.$router)
    },
  },
  mounted() {
    try {
      this.menusList = JSON.parse(localStorage.getItem('menusList')) || []
    } catch (e) {
      this.menusList = []
    }
    this.userName = localStorage.getItem('userName') || ''
    this.loginName = localStorage.getItem('loginName') || ''
    // ponytail: 刷新时展开当前路由所在的分组，其余全部折叠
    const path = this.$route.path
    const activeGroup = MENU_TREE.find(group => {
      if (group.children) return group.children.some(item => path.startsWith(item.path))
      if (group.sections) return group.sections.some(s => s.children.some(item => path.startsWith(item.path)))
      return false
    })
    if (activeGroup) {
      this.expandedGroups = { [activeGroup.groupKey]: true }
    }
  },
}
</script>

<style scoped>
.console-side-menu {
  width: 240px;
  height: 100vh;
  background: #0A1628;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(42, 98, 255, 0.12);
  transition: width 0.25s ease;
  position: relative;
  flex-shrink: 0;

  &.collapsed {
    width: 36px;
    min-width: 36px;
    align-items: center;
    overflow: hidden;
  }
}

/* 顶部 logo */
.side-logo {
  height: 56px;
  min-height: 56px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid rgba(42, 98, 255, 0.12);
  background: linear-gradient(90deg, rgba(42, 98, 255, 0.10) 0%, transparent 100%);
  flex-shrink: 0;
}

.side-logo-img {
  height: 32px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  filter: drop-shadow(0 0 12px rgba(42, 98, 255, 0.25));
}

/* 看板入口 — 一级菜单 */
.dashboard-entry {
  height: 48px;
  line-height: 48px;
  padding: 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  user-select: none;
  transition: all 0.2s;
  flex-shrink: 0;
  position: relative;
}

.dashboard-entry:hover {
  background: rgba(42, 98, 255, 0.12);
  color: rgba(255, 255, 255, 0.95);
}
.dashboard-entry:focus-visible,
.group-header:focus-visible,
.menu-item:focus-visible {
  outline: 2px solid #8AB4FF;
  outline-offset: -2px;
}

.dashboard-entry.active {
  color: #8AB4FF;
  background: rgba(42, 98, 255, 0.2);
}

.dashboard-entry-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: inherit;
  flex-shrink: 0;
}

.dashboard-entry-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}
.collapse-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.08);
  transition: all 0.2s;
  z-index: 10;

  &:hover {
    background: rgba(42, 98, 255, 0.3);
    color: #8AB4FF;
  }

  .collapsed & {
    position: static;
    margin-top: 8px;
  }
}
.menu-group {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.group-header {
  height: 48px;
  line-height: 48px;
  padding: 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  display: flex;
  align-items: center;
  user-select: none;
  transition: background 0.2s;
}
.group-header:hover {
  background: rgba(42, 98, 255, 0.12);
}
.arrow {
  font-size: 10px;
  margin-right: 8px;
  transition: transform 0.2s;
  display: inline-block;
  color: rgba(255, 255, 255, 0.35);
}
.arrow.expanded {
  transform: rotate(0deg);
}
.arrow:not(.expanded) {
  transform: rotate(-90deg);
}
.group-children {
  padding-bottom: 4px;
}
.menu-item {
  height: 40px;
  line-height: 40px;
  padding: 0 16px 0 44px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  border-radius: 0 4px 4px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.menu-item-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
}
.menu-item-icon >>> svg {
  display: block;
}
.menu-item-text {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.menu-item:hover {
  background: rgba(42, 98, 255, 0.12);
  color: rgba(255, 255, 255, 0.9);
}
.menu-item.active {
  color: #8AB4FF;
  font-weight: 600;
  background: rgba(42, 98, 255, 0.2);
}

/* 系统设置二级分区标题 — 浅色文字 + 分割线 */
.section-divider {
  height: 32px;
  line-height: 32px;
  padding: 0 16px 0 44px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 1px;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  margin: 4px 0 2px;
}

/* 二级分区内的子项 — 在缩进基础上再缩进一级 */
.section-item {
  padding-left: 56px !important;
}

/* 底部用户区域 — 固定在窗口底部 */
.side-user {
  flex-shrink: 0;
  position: relative;
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(8, 18, 36, 0.5);
  cursor: pointer;
}

.side-user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.side-user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2A62FF, #5584FF);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
}

.side-user-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.side-user-name {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.side-user-role {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 用户浮层 — 向上展开，避免超出窗口底边 */
.side-user-popup {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 16px;
  right: 16px;
  background: rgba(8, 18, 36, 0.95);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(42, 98, 255, 0.15);
  border-radius: 8px;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  z-index: 1000;
}

.side-user-popup-body,
.side-user-popup-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  transition: background 0.2s;
}

.side-user-popup-body:hover {
  background: rgba(42, 98, 255, 0.1);
  color: rgba(255, 255, 255, 0.95);
}

.side-user-popup-divider {
  height: 1px;
  margin: 0 12px;
  background: rgba(42, 98, 255, 0.1);
}

.side-user-popup-footer {
  color: rgba(255, 100, 100, 0.75);
}

.side-user-popup-footer:hover {
  background: rgba(255, 68, 68, 0.1);
  color: #FF6B6B;
}

.side-popup-arrow {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.25);
}

.side-user-popup-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.side-user-popup-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.side-user-popup-enter-from,
.side-user-popup-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
